import React from 'react';
import { Card, Button, Badge } from 'react-bootstrap';
import { Link, LinkProps } from 'react-router-dom';
import { Calendar, MapPin, Music } from 'react-feather';
import { useTranslation } from 'react-i18next';
// Fix for as={Link} type issues
type ButtonProps = {
  as?: React.ElementType;
};

interface EventCardProps {
  id: string;
  name: string;
  description?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
  active: boolean;
  djName?: string;
  djUsername?: string;
  djVerified?: boolean;
  requestCount?: number;
  isOwner?: boolean;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const EventCard: React.FC<EventCardProps> = ({
  id,
  name,
  description,
  location,
  startDate,
  endDate,
  active,
  djName,
  djUsername,
  djVerified,
  requestCount = 0,
  isOwner = false,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(navigator.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <Card className="h-100 shadow-sm event-card">
      {active ? (
        <div className="position-absolute top-0 end-0 m-2">
          <Badge bg="success">{t('event.active')}</Badge>
        </div>
      ) : (
        <div className="position-absolute top-0 end-0 m-2">
          <Badge bg="secondary">{t('event.inactive')}</Badge>
        </div>
      )}
      
      <Card.Body>
        <Card.Title className="d-flex align-items-center mb-3">
          <Music className="me-2 text-primary" size={20} />
          {name}
        </Card.Title>
        
        {description && <Card.Text className="text-muted small mb-3">{description}</Card.Text>}
        
        <div className="mb-3">
          {location && (
            <div className="d-flex align-items-center mb-2 small text-muted">
              <MapPin size={16} className="me-2" />
              {location}
            </div>
          )}
          
          {startDate && (
            <div className="d-flex align-items-center small text-muted">
              <Calendar size={16} className="me-2" />
              {formatDate(startDate)}
              {endDate && ` - ${formatDate(endDate)}`}
            </div>
          )}
        </div>
        
        {djName && djUsername && (
          <div className="d-flex align-items-center mb-3">
            <Link 
              to={`/dj/${djUsername}`} 
              className="text-decoration-none d-flex align-items-center"
            >
              {djName}
              {djVerified && (
                <Badge bg="primary" className="ms-2">
                  {t('event.verified')}
                </Badge>
              )}
            </Link>
          </div>
        )}
        
        <div className="d-flex justify-content-between align-items-center mt-3">
          {isOwner ? (
            <div className="d-flex">
              <Button 
                variant="outline-primary" 
                size="sm" 
                className="me-2"
                onClick={() => onEdit && onEdit(id)}
              >
                {t('event.edit')}
              </Button>
              <Button 
                variant="outline-danger" 
                size="sm"
                onClick={() => onDelete && onDelete(id)}
              >
                {t('event.delete')}
              </Button>
            </div>
          ) : (
            <Badge bg="info">
              {requestCount} {requestCount === 1 ? t('event.request') : t('event.requests')}
            </Badge>
          )}
          
          <Button 
            as={Link as unknown as any} 
            to={`/request/${id}`} 
            variant={isOwner ? "primary" : "outline-primary"} 
            size="sm"
          >
            {isOwner ? t('event.viewRequests') : t('event.makeRequest')}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EventCard;
